import React from 'react';
import styled from '@emotion/styled';
import { bool, shape, string, arrayOf, number, oneOfType, object, func, array } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { noop } from 'lodash';
import {
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  DataTableSkeleton,
  Loading,
} from '@carbon/react';
import { Pencil, Package, ArrowUpNarrowWide, ArrowDownWideNarrow, ArrowDownUp } from 'lucide-react';

import useNumberUtils from 'Hooks/useNumberUtils';
import Img from 'Containers/Image/Image';
import ReservedProductsPagination from 'Containers/ReservedProducts/ReservedProductsPagination';
import FilterReservedProducts from 'Containers/ReservedProducts/FilterReservedProducts';
import QuantitySelect from 'Containers/QuantitySelector/QuantitySelect';
import { AnalyticsLink } from 'Components/shared';

const StyledReservedProducts = styled.div`
  .cds--data-table {
    font-size: 16px;
    border: 1px solid #e2e2e2;

    thead {
      background: #e0e0e0;
      font-weight: 600;

      th {
        color: #3a3a3a;
        font-size: 16px;
        font-weight: 600;
        background: #f5f7f9;
        text-align: center;

        .cds--table-header-label {
          font-size: 16px;
          font-weight: 500;

          button {
            width: 100%;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            background: transparent;
            border: none;
            gap: 5px;
            font-size: inherit;
            font-weight: inherit;

            svg {
              width: 18px;
              height: 18px;
              stroke: #707070;
              min-width: 18px;
            }
          }
        }
      }
    }

    tbody {
      tr {
        td {
          background: #fff;
          text-align: center;

          &:first-of-type {
            width: 40%;
          }

          &:last-of-type {
            a {
              display: flex;
              justify-content: center;
              gap: 5px;
              align-items: center;
              background-color: #fcfdff;
              border: 1px solid #dde6f2;
              color: #3a3a3a;
              padding: 11px 17px;
            }
          }

          .mobileLabel {
            font-weight: 600;
            margin-right: 5px;
            line-height: 24px;
            display: none;
          }

          .productCode {
            display: flex;
            align-items: center;
            padding: 16px 0;

            .preview {
              display: flex;
              justify-content: center;
              align-items: center;
              margin-right: 15px;
              width: 120px;
              height: 120px;
              padding: 10px;
              min-width: 120px;
              border: 1px solid ${ props => props.theme.products.colors.border };
              //background-color: ${ props => props.theme.error ? props.theme.colors.white : 'transparent' };
              background-color: ${ props => props.theme.colors.white };

              img {
                display: block;
                height: auto;
                max-height: 100%;
                max-width: 100%;
                width: auto;
              }

              .cds--loading svg {
                max-width: 100%;
              }

              svg:not(.cds--loading__svg) {
                position: relative;
                display: block;
                margin: 0 auto;
                height: auto;
                width: auto;
                max-height: 62%;
                max-width: 100%;
              }
            }

            .details {
              text-align: left;

              a {
                color: inherit;
              }

              p {
                color: inherit;
                text-align: left;
                font-weight: 600;
                font-size: 14px;;

                span {
                  font-weight: normal;
                }
              }

              .title {
                &:hover {
                  color: ${ props => props.theme.colors.blue };
                }

                margin-bottom: 5px;
                display: -webkit-box;
                -webkit-line-clamp: 3;
                -webkit-box-orient: vertical;
                overflow: hidden;
                font-weight: normal;
                font-size: 0.875rem;
                line-height: 1.175rem;
                word-break: break-word;
              }
            }
          }

          .quantity {
            .quantityMobile {
              display: none;
            }

            .quantitySelector {
              input:disabled {
                width: 50px;
                height: 45px;
              }
            }
          }
        }
      }
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.laptop }) {
    .pageTitle {
      margin-bottom: 15px;
    }

    .cds--skeleton.cds--data-table-container {
      overflow: hidden;
      max-width: calc(100vw - 34px);
    }

    .cds--data-table:not(.cds--skeleton) {
      border: none;

      > thead {
        display: none;
      }

      > tbody {
        background: #fff;

        tr {
          border: 1px solid #e2e2e2;
          display: flex;
          flex-direction: column;
          height: auto;
          margin-top: 20px;

          &:hover {
            background: #fff;
          }

          &:first-of-type {
            &:hover td {
              border: none;
            }

            td {
              border: none;
            }
          }

          td {
            border: none;
            text-align: left;

            &:first-of-type {
              width: 100%;
            }

            &:last-of-type {
              padding: 16px;
            }

            .mobileLabel {
              display: inline;
            }

            .productCode {
              .preview {
                min-width: 75px;
                width: 75px;
                height: 75px;
              }

              .details {
                p {
                  display: none;
                }
              }
            }

            .quantity {
              .quantityMobile {
                display: block;
              }

              .quantitySelector {
                display: none;
              }
            }
          }
        }
      }
    }
  }
`;

const StyledTableCell = styled(TableCell)`
`;

const ReservedProductsComponent = ({
  reservedProducts,
  loading,
  pagination,
  sortOptions,
  defaultSortId,
  currentSort,
  handleMobileOrderChange,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatDate } = useNumberUtils();

  const headerData = [
    {
      key: 'productCode',
      header: t('pages.product'),
    },
    {
      key: 'quantity',
      header: t('pages.quantity'),
    },
    {
      key: 'expirationDate',
      header: t('pages.expirationDate'),
    },
    {
      key: 'orderNumber',
      header: t('pages.orderNumber'),
    },
    {
      key: 'actions',
      header: t('pages.actions'),
    },
  ];

  const mapHeaderToLabel = label => {
    let mappedLabel = label;
    if (label === 'productCode') {
      mappedLabel = 'product';
    }
    return t(`pages.${mappedLabel}`);
  };

  const renderTableHeader = header => {
    const mappedSortingParams = {
      productCode: 'product',
      quantity: 'quantity',
      expirationDate: 'expiration_date',
      orderNumber: 'order_number',
    };

    const [orderBy, orderDirection] = currentSort.id.split('-');
    const isSorted = orderBy === mappedSortingParams[header.key];
    const isAsc = orderDirection === 'asc';

    let sortIcon = <ArrowDownUp />;
    let sortDirection = 'asc';

    if (isSorted && currentSort.id !== defaultSortId) {
      sortIcon = isAsc ? <ArrowUpNarrowWide /> : <ArrowDownWideNarrow />;
      sortDirection = isAsc ? 'desc' : 'none';
    }

    if (isSorted && currentSort.id === defaultSortId) {
      sortIcon = <ArrowDownWideNarrow />;
      sortDirection = 'asc';
    }

    return (header.key === 'dropShipment' || header.key === 'actions') ? <span>{ header.header }</span> : (
      <button type="button" onClick={ () => handleMobileOrderChange(`${ mappedSortingParams[header.key] }-${ sortDirection }`) }>
        <span>{ header.header }</span>
        { sortIcon }
      </button>
    );
  };

  const renderCell = cell => {
    if (cell.info.header === 'productCode') {
      const { title, slug, productId, erpProductId, urlThumbnailPicture } = cell.value;
      return (
        <div className="productCode">
          <Link
            className="preview"
            to={ `/products/${ slug }/pd/${ productId }` }
            state={ {
              component: 'reserved_products',
              source: 'reserved_products_table',
              clickedOn: 'image',
              productId
            } }
          >
            <Img
              className="image"
              src={ urlThumbnailPicture }
              unloader={ <Package size={ 50 } strokeWidth={ 1.25 } /> }
              loading="lazy"
              loader={ <Loading withOverlay={ false } small /> }
              alt={ title }
            />
          </Link>
          <div className="details">
            <Link
              className="title"
              to={ `/products/${ slug }/pd/${ productId }` }
              state={ {
                component: 'reserved_products',
                source: 'reserved_products_table',
                clickedOn: 'title',
                productId
              } }
            >
              { title }
            </Link>
            <p>{ t('orders.productCode') }: <span>{ erpProductId }</span></p>
          </div>
        </div>
      );
    }

    if (cell.info.header === 'quantity') {
      const { quantity, erpProductId } = cell.value;
      return (
        <div className="quantity">
          <div className="quantityMobile">
            <div>
              <span className="mobileLabel">{ t('orders.productCode') }:</span>
              { erpProductId }
            </div>
            <div>
              <span className="mobileLabel">{ t('pages.quantity') }:</span>
              { quantity }
            </div>
          </div>
          <QuantitySelect
            size="lg"
            disabled
            value={ quantity }
            min={ 1 }
            step={ 1 }
            max={ 100 }
            onChange={ noop }
          />
        </div>
      );
    }

    if (cell.info.header === 'expirationDate') {
      return (
        <>
          <span className="mobileLabel">{ t(`orders.${ cell.info.header }`) }:</span>
          { cell.value ? formatDate(cell.value, { timeStyle: undefined }) : 'N/A' }
        </>
      );
    }

    if (cell.info.header === 'actions') {
      return (
        <AnalyticsLink
          to={ `/orders/${ cell.value }` }
          event={ { action: 'edit_reservation_clicked' } }
        >
          <Pencil />
          { t('orders.editReservation') }
        </AnalyticsLink>
      );
    }

    return (
      <>
        <span className="mobileLabel">{ t(`orders.${ cell.info.header }`) }:</span>
        { cell.value }
      </>
    );
  };

  return (
    <StyledReservedProducts theme={ theme }>
      <div className="pageTitle">
        <h3>{ t('common.pages.reservedProducts') }</h3>
      </div>
      {/* <NWNotification errors={ errors } delay={ 10 } /> */}
      <FilterReservedProducts
        sortOptions={ sortOptions }
        currentSort={ currentSort }
        handleMobileOrderChange={ handleMobileOrderChange }
      />
      { loading ? (
        <DataTableSkeleton showToolbar={ false } showHeader={ false } />
      ) : (
        <DataTable rows={ reservedProducts } headers={ headerData }>
          {({ rows, headers, getHeaderProps, getTableProps, getRowProps }) => (
            <TableContainer>
              <Table { ...getTableProps() }>
                <TableHead>
                  <TableRow>
                    {headers.map(header => (
                      <TableHeader
                        { ...getHeaderProps({ header }) }
                      >
                        { renderTableHeader(header) }
                      </TableHeader>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  { reservedProducts.length === 0 && (<TableRow><TableCell colSpan={ headers.length + 1 }>{ t('orders.noResultsFound') }</TableCell></TableRow>) }
                  {rows.map(row => (
                    <React.Fragment key={ row.id }>
                      <TableRow
                        { ...getRowProps({ row }) }
                      >
                        { row.cells.map(cell => (
                          <StyledTableCell
                            data-label={ cell.info.header }
                            name={ () => mapHeaderToLabel(cell.info.header) }
                            key={ cell.id }
                          >
                            { renderCell(cell) }
                          </StyledTableCell>
                        ))}
                      </TableRow>
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DataTable>
      )}
      <ReservedProductsPagination loading={ loading } { ...pagination } />
    </StyledReservedProducts>
  );
};

ReservedProductsComponent.propTypes = {
  reservedProducts: arrayOf(shape({
    id: oneOfType([number, string]),
    productCode: object,
    quantity: object,
    actions: oneOfType([number, string]),
  })),
  loading: bool,
  pagination: shape({
    currentPage: number,
    recordsPerPage: number,
    totalPages: number,
    totalRecords: number,
    loading: bool,
  }),
  sortOptions: array,
  defaultSortId: string,
  currentSort: object,
  handleMobileOrderChange: func,
};

ReservedProductsComponent.displayName = 'ReservedProductsComponent';

export default ReservedProductsComponent;
